import React, { useState, useEffect } from 'react';
import { MdAdd, MdDelete, MdEdit, MdDownload, MdClose, MdInsertDriveFile, MdDateRange } from 'react-icons/md';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import DeleteModel from '../Common/DeleteModel';
import FileActions from '../Common/FileActions';
import { formatUtcToLocalDateTime, formatUtcToLocalDate } from '../../Utils/DateTime';
import { uploadOrganizationDocuments, getAllDocumentsByOrgId, getDocumentById, deleteDocumentById, downloadOrganizationDocument } from '../../Services/OrganizationService';
import { useAuth } from '../../Context/AuthContext';
import { toast } from 'react-toastify';
import CustomDatePicker from '../Common/CustomDatePicker';

const validationSchema = Yup.object({
  title: Yup.string().trim().required('Document title is required'),
  statusId: Yup.number().oneOf([1, 61]),
  statusId: Yup.number()
    .oneOf([1, 61])
    .transform((value, originalValue) => {
      if (originalValue === '') return undefined;
      return Number(originalValue);
    }),
  file: Yup.mixed()
    .nullable()
    .test('fileType', 'Only PDF files are accepted', (value) => {
      if (!value) return true;
      return value.type === 'application/pdf' || value.name?.toLowerCase().endsWith('.pdf');
    }),
  effectiveDate: Yup.string(),
  terminationDate: Yup.string(),
});

const initialDocumentValues = {
  title: '',
  statusId: 1,
  file: null,
  effectiveDate: null,
  terminationDate: null,
};

export default function Documents({ onSaveSuccess, onContinue, organizationId }) {
  const [documents, setDocuments] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingPresetKey, setEditingPresetKey] = useState(null);
  const [editingFileName, setEditingFileName] = useState(null);
  const [editingFileId, setEditingFileId] = useState(null);
  const [editingLoadingId, setEditingLoadingId] = useState(null);
  const [metadata, setMetadata] = useState([]);
  const [currentMetadata, setCurrentMetadata] = useState({
    key: '',
    value: '',
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [docsLoading, setDocsLoading] = useState(false);
  const [pendingPresetKey, setPendingPresetKey] = useState(null);
  const { token } = useAuth();

  const PRESET_DOCUMENTS = [
    { key: 'cliaCertificate', title: 'CLIA Certificate' },
    { key: 'cliaWaiverCertificate', title: 'CLIA Waiver Certificate' },
    { key: 'voidedCheck', title: 'Voided Check' },
    { key: 'w9', title: 'W9' },
  ];

  const parseMetadata = (meta) => {
    if (!meta) return [];
    try {
      if (typeof meta === 'string') {
        const parsed = JSON.parse(meta);
        if (Array.isArray(parsed)) return parsed;
        if (typeof parsed === 'object') return Object.keys(parsed).map((k) => ({ key: k, value: parsed[k] }));
      }
      if (Array.isArray(meta)) return meta;
      if (typeof meta === 'object') return Object.keys(meta).map((k) => ({ key: k, value: meta[k] }));
    } catch (err) {
      console.error('Failed to parse metadata', err);
      return [];
    }
    return [];
  };

  const getPresetKeyFromTitle = (title) => {
    if (!title) return null;
    const normalized = String(title).trim().toLowerCase();
    const preset = PRESET_DOCUMENTS.find((item) => item.title.toLowerCase() === normalized);
    return preset?.key ?? null;
  };

  const getPresetTitleFromKey = (key) => PRESET_DOCUMENTS.find((item) => item.key === key)?.title ?? null;

  const mapApiDocumentToUi = (doc) => {
    if (!doc) return null;
    const id = doc.id ?? doc.Id ?? null;
    const fileName = doc.file_name ?? doc.fileName ?? null;
    const metadata = parseMetadata(doc.metadata ?? doc.Metadata ?? null);
    const rawEffective = doc.effective_start_date ?? doc.effectiveStartDate ?? doc.effectiveDate ?? null;
    const rawTermination = doc.termination_date ?? doc.terminationDate ?? null;
    const rawUploaded = doc.created_on ?? doc.createdOn ?? null;

    const effectiveDate = rawEffective;
    const terminationDate = rawTermination;
    const uploadedDate = rawUploaded;

    const effectiveDateDisplay = rawEffective ? formatUtcToLocalDate(rawEffective) : null;
    const terminationDateDisplay = rawTermination ? formatUtcToLocalDate(rawTermination) : null;
    const uploadedDateDisplay = rawUploaded ? formatUtcToLocalDateTime(rawUploaded) : null;

    return {
      id,
      organization_id: doc.organization_id ?? doc.OrganizationId ?? null,
      fileId: doc.file_id ?? doc.fileId ?? null,
      file_id: doc.file_id ?? doc.fileId ?? null,
      status_id: doc.status_id ?? doc.StatusId ?? null,
      type: doc.type ?? null,
      title: doc.title ?? doc.Title ?? '',
      fileName,
      metadata,
      effectiveDate,
      effectiveDateDisplay,
      effective_start_date: doc.effective_start_date ?? doc.effectiveStartDate ?? null,
      terminationDate,
      terminationDateDisplay,
      termination_date: doc.termination_date ?? doc.terminationDate ?? null,
      uploadedDate,
      uploadedDateDisplay,
      raw: doc,
    };
  };

  const handleAddMetadata = () => {
    if (currentMetadata.key.trim() && currentMetadata.value.trim()) {
      setMetadata((prev) => [...prev, { ...currentMetadata }]);
      setCurrentMetadata({ key: '', value: '' });
    }
  };

  const handleRemoveMetadata = (index) => {
    setMetadata((prev) => prev.filter((_, i) => i !== index));
  };

  const handleMetadataChange = (field) => (event) => {
    setCurrentMetadata((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const resetModalForm = () => {
    formik.resetForm();
    setMetadata([]);
    setCurrentMetadata({ key: '', value: '' });
    setEditingId(null);
    setEditingPresetKey(null);
    setEditingFileName(null);
    setEditingFileId(null);
    formik.setFieldValue('statusId', 1);
  };

  const handleOpenModal = () => {
    setPendingPresetKey(null);
    resetModalForm();
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetModalForm();
    setPendingPresetKey(null);
  };

  const handlePresetCheckboxChange = (presetKey) => (event) => {
    const checked = event.target.checked;
    const presetTitle = getPresetTitleFromKey(presetKey);
    const documentAlreadyExists = documents.some((doc) => getPresetKeyFromTitle(doc.title) === presetKey);

    if (checked) {
      if (documentAlreadyExists) {
        return;
      }

      resetModalForm();
      setPendingPresetKey(presetKey);
      formik.setValues({
        title: presetTitle,
        statusId: 1,
        file: null,
        effectiveDate: null,
        terminationDate: null,
      });
      setShowModal(true);
      return;
    }

    if (pendingPresetKey === presetKey) {
      setPendingPresetKey(null);
      setShowModal(false);
      resetModalForm();
    }
  };

  const handleSubmitDocument = async (values) => {
    const statusId = Number(values.statusId);
    const requiresUpload = statusId === 1 && !editingId;
    if (requiresUpload && !formik.values.file) {
      formik.setFieldError('file', 'Please upload a document');
      formik.setFieldTouched('file', true);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        id: editingId || null,
        organization_id: organizationId || null,
        file_id: null,
        status_id: statusId || 1,
        title: values.title,
        effective_start_date: values.effectiveDate || null,
        termination_date: values.terminationDate || null,
        type: editingId ? 'update' : 'create',
        metadata: metadata || [],
      };

      const formData = new FormData();
      if (formik.values.file) {
        formData.append('file', formik.values.file);
      }
      formData.append('request', JSON.stringify(payload));

      try {
        const res = await uploadOrganizationDocuments(formData, token);
        if (res?.is_success) {
          const doc = res.data ?? res;
          const mapped = mapApiDocumentToUi(doc);
          if (!editingId) {
            setDocuments((prev) => [...prev, mapped]);
          }
          await loadDocuments();
          handleCloseModal();
          toast.success(res?.message || (editingId ? 'Document updated successfully' : 'Document uploaded successfully'));
          return;
        } else if (res?.is_success === false && res?.status_code === 500) {
          toast.error('Internal Server Error. Please Contact Support Team.');
          console.error('Internal Server Error. Please Contact Support Team.', res);
        } else {
          toast.error(res?.message || (editingId ? 'Failed to update document' : 'Failed to upload document'));
          console.error('Failed to save document', res);
        }
      } catch (err) {
        console.error('Error uploading document:', err);
      }
    } catch (error) {
      console.error('Error saving document:', error);
    } finally {
      setLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: initialDocumentValues,
    validationSchema,
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: handleSubmitDocument,
  });

  // Edit document
  const handleEditDocument = (document) => {
    // Show spinner on the edit icon and open modal; fetch extra details in background
    setEditingLoadingId(document.id);
    const fileName = document.fileName ?? document.file_name ?? null;
    const presetKey = getPresetKeyFromTitle(document.title);

    formik.setValues({
      title: document.title,
      statusId: document.status_id ?? 1,
      file: null,
      effectiveDate: document.effectiveDate,
      terminationDate: document.terminationDate,
    });
    setMetadata(document.metadata || []);
    setEditingId(document.id);
    setEditingPresetKey(presetKey);
    setEditingFileName(fileName);
    setEditingFileId(document.file_id ?? document.fileId ?? null);
    setShowModal(true);

    (async () => {
      try {
        const res = await getDocumentById(document.id, token);
        if (res?.is_success) {
          const d = res.data ?? res;
          const fetchedName = d.file_name ?? d.fileName ?? null;
          if (fetchedName) setEditingFileName(fetchedName);
        }
      } catch (err) {
        // ignore fetch errors
      } finally {
        setEditingLoadingId(null);
      }
    })();
  };

  const handleDownloadExisting = async () => {
    if (!editingFileId) return;
    setEditingLoadingId(editingFileId);
    try {
      const fileId = editingFileId;
      const fileName = editingFileName || `document-${editingFileId}`;

      if (!fileId) {
        toast.info('No downloadable file available for this document');
        return;
      }
      const dl = await downloadOrganizationDocument(fileId, token);
      const payload = dl?.data ?? dl;
      const dataStr = typeof payload === 'string' ? payload : (payload?.file || payload?.data || null);

      if (!dataStr || typeof dataStr !== 'string') {
        toast.error('Download failed: no file returned');
        return;
      }

      if (dataStr.startsWith('data:')) {
        // create anchor with data URI
        const link = document.createElement('a');
        link.href = dataStr;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('Downloaded Successfully');
        return;
      }
      const ext = (fileName.split('.').pop() || '').toLowerCase();
      let mime = 'application/octet-stream';
      if (ext === 'pdf') mime = 'application/pdf';
      else if (ext === 'doc') mime = 'application/msword';
      else if (ext === 'docx') mime = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      else if (ext === 'xlsx') mime = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      else if (ext === 'xls') mime = 'application/vnd.ms-excel';
      else if (ext === 'txt') mime = 'text/plain';

      const href = `data:${mime};base64,${dataStr}`;
      const link = document.createElement('a');
      link.href = href;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Downloaded Successfully');
    } catch (err) {
      console.error('Download error', err);
      toast.error('Error downloading document');
    } finally {
      setEditingLoadingId(null);
    }
  };

  const handleDeleteDocument = (document) => {
    setDocumentToDelete(document);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!documentToDelete) return;

    setLoading(true);
    try {
      // Call API to delete
      const res = await deleteDocumentById(documentToDelete.id, token);
      if (res?.is_success) {
        toast.success(res?.message || 'Document deleted successfully');
        setShowDeleteModal(false);
        setDocumentToDelete(null);
        await loadDocuments();
      } else if (res?.is_success === false && res?.status_code === 500) {
        toast.error('Internal Server Error. Please Contact Support Team.');
        console.error('Failed to delete document', res);
      } else {
        console.error('Failed to delete document', res);
      }
    } catch (error) {
      console.error('Error deleting document:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load documents for organization (reusable)
  const loadDocuments = async () => {
    if (!organizationId) return;
    setDocsLoading(true);
    try {
      const res = await getAllDocumentsByOrgId(organizationId, token);
      if (res?.is_success) {
        const items = Array.isArray(res?.data?.documents)
          ? res.data.documents
          : Array.isArray(res.data)
            ? res.data
            : Array.isArray(res?.data?.data)
              ? res.data.data
              : [];
        const mapped = items.map(mapApiDocumentToUi).filter(Boolean);
        setDocuments(mapped);
      } else {
        console.error('Failed to load documents', res);
        setDocuments([]);
      }
    } catch (err) {
      console.error('Error loading documents:', err);
      setDocuments([]);
    } finally {
      setDocsLoading(false);
    }
  };

  React.useEffect(() => {
    loadDocuments();
  }, [organizationId, token]);

  const existingPresetKeys = React.useMemo(
    () => new Set(documents.map((doc) => getPresetKeyFromTitle(doc.title)).filter(Boolean)),
    [documents]
  );

  return (
    <div className="py-3 px-0 px-md-3">
      <div className="p-4 rounded-4 border bg-light-subtle">
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-3">
          <h6 className="fw-bold hc-text-primary mb-0">Documents</h6>
          <button
            type="button"
            className="btn hc-bg-primary text-white rounded-3 d-flex align-items-center gap-2 fw-semibold"
            onClick={handleOpenModal}
          >
            <MdAdd size={20} />
            Add Document
          </button>
        </div>

        <div className="card rounded-4 border shadow mb-4">
          <div className="card-body">
            <div className="row g-3">
              {PRESET_DOCUMENTS.map((preset) => {
                const isChecked = existingPresetKeys.has(preset.key) || pendingPresetKey === preset.key;
                return (
                  <div key={preset.key} className="col-12 col-md-6">
                    <div className="card rounded-4 hc-bg-primary text-white border-0 h-100">
                      <div className="card-body p-3 d-flex align-items-center gap-2">
                        <div>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={isChecked}
                            onChange={handlePresetCheckboxChange(preset.key)}
                            disabled={existingPresetKeys.has(preset.key) && !pendingPresetKey}
                            style={{ width: '1.05rem', height: '1.05rem', accentColor: 'var(--hc-success-green)' }}
                          />
                        </div>
                        <div>
                          <div className="fw-semibold mb-0 text-white">{preset.title}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Documents List */}
        {docsLoading ? (
          <div className="text-center py-5 text-muted">
            <div className="spinner position-absolute d-flex align-items-center justify-content-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          </div>
        ) : documents.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <p>No documents uploaded yet.</p>
          </div>
        ) : (
          <div className="row g-3">
            {documents.map((document, index) => (
              <div key={document.id} className="col-12 col-md-6 col-lg-4">
                <div className="card shadow-lg h-100 rounded-4 shadow-sm border-0">
                  <div className="card-body d-flex flex-column">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <h6 className="fw-semibold card-title mb-0 flex-grow-1">{document.title}</h6>
                      <div className="d-flex gap-1">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary rounded-3"
                          onClick={() => handleEditDocument(document)}
                          title="Edit document"
                        >
                          {editingLoadingId === document.id ? (
                            <span className="spinner-border spinner-border-sm text-primary" role="status" aria-hidden="true" />
                          ) : (
                            <MdEdit size={16} />
                          )}
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger rounded-3"
                          onClick={() => handleDeleteDocument(document)}
                          title="Delete document"
                        >
                          <MdDelete size={16} />
                        </button>
                      </div>
                    </div>

                    <div className="small text-muted mb-3">
                      <div className="mb-2">
                        <MdInsertDriveFile className="me-2 align-middle" style={{ color: 'var(--hc-icon-blue-doc)' }} />
                        {document.fileName || "N/A"}
                      </div>
                      <div className="mb-2">
                        <MdDateRange className="me-2 align-middle" style={{ color: 'var(--hc-icon-blue-doc)' }} />
                        {document.uploadedDateDisplay || "N/A"}
                      </div>
                    </div>

                    {(document.effectiveDate || document.terminationDate) && (
                      <div className="mb-2">
                        {document.effectiveDate && (
                          <div className="small mb-2">
                            <span className="text-muted">Effective:</span>
                            <span className="ms-2 fw-semibold text-success">
                              {document.effectiveDateDisplay}
                            </span>
                          </div>
                        )}
                        {document.terminationDate && (
                          <div className="small">
                            <span className="text-muted">Termination:</span>
                            <span className="ms-2 fw-semibold text-danger">
                              {document.terminationDateDisplay}
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Metadata Display */}
                    {document.metadata && document.metadata.length > 0 && (
                      <div className="mt-auto">
                        <small className="text-muted d-block mb-2">Metadata:</small>
                        <div className="d-flex flex-wrap gap-2">
                          {document.metadata.map((meta, idx) => (
                            <span
                              key={idx}
                              className="badge bg-light text-dark border border-secondary small"
                            >
                              {meta.key}: {meta.value}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Continue Button */}
      <div className="mt-4 d-flex gap-2 justify-content-end">
        <button
          type="button"
          className="btn hc-bg-primary text-white px-5 rounded-3 fw-semibold"
          onClick={onContinue}
        >
          Continue
        </button>
      </div>

      {/* Add/Edit Document Modal */}
      {showModal && (
        <div
          className="modal d-block m-0"
          tabIndex="-1"
          style={{ background: 'rgba(var(--hc-black-rgb), 0.25)' }}
          role="dialog"
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content rounded-4 shadow-lg">
              <div className="modal-header border-0 pb-2 d-flex justify-content-between align-items-center">
                <h5 className="modal-title fw-bold mb-0">
                  {editingId ? 'Edit Document' : 'Add Document'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={handleCloseModal}
                  disabled={loading}
                />
              </div>

              <form onSubmit={formik.handleSubmit}>
                <div className="modal-body pt-0">
                  {/* Title Field */}
                  <div className="row g-3 mb-3">
                    <div className="col-md-8">
                      <label className="form-label fw-semibold">
                        Title <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-control rounded-3 ${formik.touched.title && formik.errors.title ? 'is-invalid' : ''}`}
                        placeholder="Enter document title"
                        name="title"
                        value={formik.values.title}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        disabled={loading}
                        readOnly={Boolean(editingPresetKey || pendingPresetKey)}
                      />
                      {formik.touched.title && formik.errors.title && (
                        <div className="invalid-feedback d-block">{formik.errors.title}</div>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-semibold">Status</label>
                      <select
                        name="statusId"
                        className="form-select rounded-3"
                        value={formik.values.statusId}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        disabled={loading}
                      >
                        <option value={1}>Active</option>
                        <option value={61}>Not Applicable</option>
                      </select>
                    </div>
                  </div>

                  {/* File Upload */}
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Upload Document {formik.values.statusId === 1 && !editingId && <span className="text-danger">*</span>}
                    </label>
                    <input
                      type="file"
                      className={`form-control rounded-3 ${formik.touched.file && formik.errors.file ? 'is-invalid' : ''}`}
                      onChange={(e) => {
                        const file = e.currentTarget.files?.[0];
                        formik.setFieldValue('file', file || null);
                      }}
                      onBlur={formik.handleBlur}
                      disabled={loading}
                      accept=".pdf"
                      name="file"
                    />
                    {formik.values.file ? (
                      <small className="text-muted d-block mt-1">Selected: {formik.values.file.name}</small>
                        ) : editingFileName ? (
                      <div className="d-flex align-items-center gap-2 mt-2">
                        <small className="text-muted">Existing: {editingFileName}</small>
                        <div>
                          <FileActions fileId={editingFileId} fileName={editingFileName} showPreview={false} compact={true} />
                        </div>
                      </div>
                    ) : null}
                    {formik.touched.file && formik.errors.file && (
                      <div className="invalid-feedback d-block">{formik.errors.file}</div>
                    )}
                  </div>

                  {/* Effective & Termination Dates */}
                  <div className="row g-2 mb-3">
                    <div className="col">
                      <label className="form-label fw-semibold">Effective Date</label>

                    <CustomDatePicker
                      name="effectiveDate"
                      formik={formik}
                      disabled={loading}
                    />

                      {/* <input
                        type="date"
                        className="form-control rounded-3"
                        name="effectiveDate"
                        value={formik.values.effectiveDate}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        disabled={loading}
                      /> */}



                    </div>
                    <div className="col">
                      <label className="form-label fw-semibold">Termination Date</label>

                        <CustomDatePicker
                      name="terminationDate"
                      formik={formik}
                      disabled={loading}
                    />
                        
                      {/* <input
                        type="date"
                        className="form-control rounded-3"
                        name="terminationDate"
                        value={formik.values.terminationDate}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        disabled={loading}
                      /> */}
                    </div>
                  </div>

                  {/* Metadata Section */}
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Metadata</label>

                    {/* Metadata Input Pairs */}
                    <div className="row g-2 mb-2">
                      <div className="col">
                        <input
                          type="text"
                          className="form-control rounded-3"
                          placeholder="Key (e.g., Version)"
                          value={currentMetadata.key}
                          onChange={handleMetadataChange('key')}
                          disabled={loading}
                        />
                      </div>
                      <div className="col">
                        <input
                          type="text"
                          className="form-control rounded-3"
                          placeholder="Value (e.g., 1.0)"
                          value={currentMetadata.value}
                          onChange={handleMetadataChange('value')}
                          disabled={loading}
                        />
                      </div>
                      <div className="col-auto">
                        <button
                          type="button"
                          className="btn btn-outline-primary rounded-3 fw-semibold d-flex align-items-center gap-2"
                          onClick={handleAddMetadata}
                          disabled={loading}
                        >
                          <MdAdd size={18} />
                          Add
                        </button>
                      </div>
                    </div>

                    {/* Metadata List */}
                    {metadata.length > 0 && (
                      <div className="bg-light rounded-3 p-3">
                        {metadata.map((meta, idx) => (
                          <div
                            key={idx}
                            className="d-flex justify-content-between align-items-center mb-2"
                          >
                            <span>
                              <strong>{meta.key}:</strong> {meta.value}
                            </span>
                            <button
                              type="button"
                              className="btn btn-sm btn-link text-danger p-0"
                              onClick={() => handleRemoveMetadata(idx)}
                              disabled={loading}
                              title="Remove metadata"
                            >
                              <MdClose size={18} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="modal-footer border-0 pt-0 d-flex justify-content-end gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-secondary px-4 rounded-3"
                    onClick={handleCloseModal}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn hc-bg-primary text-white px-4 rounded-3 fw-semibold d-flex align-items-center gap-2"
                    disabled={loading}
                  >
                    {loading && (
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      />
                    )}
                    {editingId ? 'Update Document' : 'Add Document'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteModel
        isOpen={showDeleteModal}
        title="Delete Document"
        description="This will permanently delete this document. This action cannot be undone."
        entityLabel="document"
        itemName={documentToDelete?.title}
        confirmText="Delete"
        onConfirm={handleConfirmDelete}
        onClose={() => {
          setShowDeleteModal(false);
          setDocumentToDelete(null);
        }}
        isLoading={loading}
      />
    </div>
  );
}
